import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Building2,
  DollarSign,
  Clock,
  FileText,
  Calendar,
  ChevronDown,
  UserCheck,
  UserX,
  Briefcase,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/Dashboard.css";

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [customerHours, setCustomerHours] = useState([]);
  const [customerSalaries, setCustomerSalaries] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());

  useEffect(() => {
    loadAvailableMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const loadAvailableMonths = async () => {
    try {
      const response = await apiService.getAvailableMonths();
      if (response.success && response.data && response.data.length > 0) {
        setAvailableMonths(response.data);
        setSelectedMonth(response.data[0]); // Set to most recent month
      } else {
        // Default to current month if no data
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}`;
        setSelectedMonth(currentMonth);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
      // Default to current month
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}`;
      setSelectedMonth(currentMonth);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load comprehensive dashboard stats
      const statsResponse = await apiService.getDashboardStats(selectedMonth);
      if (statsResponse.success) {
        setDashboardData(statsResponse.data);
      }

      // Load customer hours breakdown
      const hoursResponse = await apiService.getCustomerHours(selectedMonth);
      if (hoursResponse.success) {
        setCustomerHours(hoursResponse.data);
      }

      // Load customer salaries breakdown
      const salariesResponse = await apiService.getCustomerSalaries(
        selectedMonth
      );
      if (salariesResponse.success) {
        setCustomerSalaries(salariesResponse.data);
      }
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₪${parseFloat(amount || 0).toLocaleString("he-IL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const toggleCustomerExpand = (customerId) => {
    setExpandedCustomers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  // Group customer hours by customer_name and aggregate sites
  const groupedCustomerHours = customerHours.reduce((acc, customer) => {
    const existingCustomer = acc.find(
      (c) => c.customer_name === customer.customer_name
    );

    if (existingCustomer) {
      existingCustomer.total_hours += customer.total_hours || 0;
      existingCustomer.kablan_hours += customer.kablan_hours || 0;
      existingCustomer.company_hours += customer.company_hours || 0;
      existingCustomer.regular_hours += customer.regular_hours || 0;
      existingCustomer.weekend_hours += customer.weekend_hours || 0;
      existingCustomer.employee_count += customer.employee_count || 0;
      existingCustomer.sites.push(customer);
    } else {
      acc.push({
        customer_id: customer.customer_id,
        customer_name: customer.customer_name,
        total_hours: customer.total_hours || 0,
        kablan_hours: customer.kablan_hours || 0,
        company_hours: customer.company_hours || 0,
        regular_hours: customer.regular_hours || 0,
        weekend_hours: customer.weekend_hours || 0,
        employee_count: customer.employee_count || 0,
        sites: [customer],
      });
    }

    return acc;
  }, []);

  // Group customer salaries by customer_name and aggregate sites
  const groupedCustomerSalaries = customerSalaries.reduce((acc, customer) => {
    const existingCustomer = acc.find(
      (c) => c.customer_name === customer.customer_name
    );

    if (existingCustomer) {
      existingCustomer.total_salary += customer.total_salary || 0;
      existingCustomer.kablan_salary += customer.kablan_salary || 0;
      existingCustomer.company_salary += customer.company_salary || 0;
      existingCustomer.employee_count += customer.employee_count || 0;
      existingCustomer.sites.push(customer);
    } else {
      acc.push({
        customer_id: customer.customer_id,
        customer_name: customer.customer_name,
        total_salary: customer.total_salary || 0,
        kablan_salary: customer.kablan_salary || 0,
        company_salary: customer.company_salary || 0,
        employee_count: customer.employee_count || 0,
        sites: [customer],
      });
    }

    return acc;
  }, []);

  const formatNumber = (value) => {
    return value.toLocaleString("he-IL", { maximumFractionDigits: 0 });
  };

  const formatMonthDisplay = (month) => {
    if (!month) return "";
    const [year, monthNum] = month.split("-");
    const date = new Date(year, parseInt(monthNum) - 1);
    return date.toLocaleDateString("he-IL", { year: "numeric", month: "long" });
  };

  if (!dashboardData && !loading) {
    return (
      <div className='dashboard'>
        <div className='dashboard-header'>
          <h1>לוח בקרה</h1>
          <p>אין נתונים זמינים</p>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard'>
      {/* Header with Month Selector */}
      <div className='dashboard-header'>
        <div>
          <h1>לוח בקרה מקיף</h1>
          <p>סטטיסטיקות ונתונים מפורטים</p>
        </div>
        <div className='dashboard-controls'>
          <div className='month-selector'>
            <Calendar size={18} />
            <button
              className='month-selector-button'
              onClick={() => setShowMonthDropdown(!showMonthDropdown)}
            >
              {formatMonthDisplay(selectedMonth)}
              <ChevronDown size={18} />
            </button>
            {showMonthDropdown && (
              <div className='month-dropdown'>
                {availableMonths.map((month) => (
                  <div
                    key={month}
                    className={`month-option ${
                      month === selectedMonth ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedMonth(month);
                      setShowMonthDropdown(false);
                    }}
                  >
                    {formatMonthDisplay(month)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען נתונים...</p>
        </div>
      ) : (
        <>
          {/* Employee Statistics */}
          <div className='dashboard-section'>
            <h2 className='section-title'>
              <Users size={24} />
              סטטיסטיקת עובדים
            </h2>
            <div className='stats-grid'>
              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#3b82f620", color: "#3b82f6" }}
                >
                  <Users size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>סה"כ עובדים</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.total || 0}
                  </h2>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#10b98120", color: "#10b981" }}
                >
                  <UserCheck size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עובדים פעילים</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.active || 0}
                  </h2>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#ef444420", color: "#ef4444" }}
                >
                  <UserX size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עובדים לא פעילים</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.inactive || 0}
                  </h2>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#f59e0b20", color: "#f59e0b" }}
                >
                  <Briefcase size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עובדים קבלן</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.by_type?.kablan || 0}
                  </h2>
                  <p className='stat-subtitle'>
                    פעילים:{" "}
                    {dashboardData?.employees?.active_by_type?.kablan || 0}
                  </p>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#8b5cf620", color: "#8b5cf6" }}
                >
                  <Building2 size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עובדים חברה</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.by_type?.company || 0}
                  </h2>
                  <p className='stat-subtitle'>
                    פעילים:{" "}
                    {dashboardData?.employees?.active_by_type?.company || 0}
                  </p>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#06b6d420", color: "#06b6d4" }}
                >
                  <Users size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>קבלן וחברה</p>
                  <h2 className='stat-value'>
                    {dashboardData?.employees?.by_type?.both || 0}
                  </h2>
                  <p className='stat-subtitle'>
                    פעילים:{" "}
                    {dashboardData?.employees?.active_by_type?.both || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hours Statistics */}
          <div className='dashboard-section'>
            <h2 className='section-title'>
              <Clock size={24} />
              סטטיסטיקת שעות - {formatMonthDisplay(selectedMonth)}
            </h2>
            <div className='stats-grid'>
              <div className='stat-card highlight'>
                <div
                  className='stat-icon'
                  style={{ background: "#3b82f620", color: "#3b82f6" }}
                >
                  <Clock size={32} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>סה"כ שעות</p>
                  <h2 className='stat-value'>
                    {formatNumber(dashboardData?.hours?.total_hours || 0)}
                  </h2>
                  <div className='stat-breakdown'>
                    <span>
                      רגילות:{" "}
                      {formatNumber(
                        dashboardData?.hours?.total_regular_hours || 0
                      )}
                    </span>
                    <span className='divider'>•</span>
                    <span>
                      סופ"ש:{" "}
                      {formatNumber(
                        dashboardData?.hours?.total_weekend_hours || 0
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#f59e0b20", color: "#f59e0b" }}
                >
                  <Briefcase size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>שעות קבלן</p>
                  <h2 className='stat-value'>
                    {formatNumber(dashboardData?.hours?.kablan_hours || 0)}
                  </h2>
                  <p className='stat-subtitle'>
                    {dashboardData?.hours?.total_hours > 0
                      ? `${(
                          (dashboardData?.hours?.kablan_hours /
                            dashboardData?.hours?.total_hours) *
                          100
                        ).toFixed(1)}% מסך השעות`
                      : "0%"}
                  </p>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#8b5cf620", color: "#8b5cf6" }}
                >
                  <Building2 size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>שעות חברה</p>
                  <h2 className='stat-value'>
                    {formatNumber(dashboardData?.hours?.company_hours || 0)}
                  </h2>
                  <p className='stat-subtitle'>
                    {dashboardData?.hours?.total_hours > 0
                      ? `${(
                          (dashboardData?.hours?.company_hours /
                            dashboardData?.hours?.total_hours) *
                          100
                        ).toFixed(1)}% מסך השעות`
                      : "0%"}
                  </p>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#10b98120", color: "#10b981" }}
                >
                  <Users size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עובדים שעבדו</p>
                  <h2 className='stat-value'>
                    {dashboardData?.hours?.employees_worked || 0}
                  </h2>
                  <p className='stat-subtitle'>
                    ממוצע:{" "}
                    {formatNumber(
                      dashboardData?.hours?.average_hours_per_employee || 0
                    )}{" "}
                    שעות
                  </p>
                </div>
              </div>
            </div>

            {/* Hours Comparison */}
            {dashboardData?.hours_comparison && (
              <div className='comparison-card'>
                <h3>השוואה לחודש קודם</h3>
                <div className='comparison-content'>
                  <div className='comparison-item'>
                    <span>חודש נוכחי:</span>
                    <strong>
                      {formatNumber(
                        dashboardData.hours_comparison.current_month_hours
                      )}{" "}
                      שעות
                    </strong>
                  </div>
                  <div className='comparison-item'>
                    <span>חודש קודם:</span>
                    <strong>
                      {formatNumber(
                        dashboardData.hours_comparison.previous_month_hours
                      )}{" "}
                      שעות
                    </strong>
                  </div>
                  <div
                    className={`comparison-item ${
                      dashboardData.hours_comparison.change >= 0
                        ? "positive"
                        : "negative"
                    }`}
                  >
                    {dashboardData.hours_comparison.change >= 0 ? (
                      <ArrowUpRight size={20} />
                    ) : (
                      <ArrowDownRight size={20} />
                    )}
                    <span>שינוי:</span>
                    <strong>
                      {formatNumber(
                        Math.abs(dashboardData.hours_comparison.change)
                      )}{" "}
                      שעות (
                      {dashboardData.hours_comparison.change_percent.toFixed(1)}
                      %)
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Salary Statistics */}
          {user?.permission !== "viewer" && (
            <div className='dashboard-section'>
              <h2 className='section-title'>
                <DollarSign size={24} />
                סטטיסטיקת משכורות - {formatMonthDisplay(selectedMonth)}
              </h2>
              <div className='stats-grid'>
                <div className='stat-card highlight'>
                  <div
                    className='stat-icon'
                    style={{ background: "#10b98120", color: "#10b981" }}
                  >
                    <DollarSign size={32} />
                  </div>
                  <div className='stat-content'>
                    <p className='stat-title'>סה"כ משכורות</p>
                    <h2 className='stat-value'>
                      {formatCurrency(
                        dashboardData?.salaries?.current_month?.total || 0
                      )}
                    </h2>
                    <p className='stat-subtitle'>
                      ממוצע:{" "}
                      {formatCurrency(
                        dashboardData?.salaries?.current_month
                          ?.average_salary || 0
                      )}
                    </p>
                  </div>
                </div>

                <div className='stat-card'>
                  <div
                    className='stat-icon'
                    style={{ background: "#f59e0b20", color: "#f59e0b" }}
                  >
                    <Briefcase size={24} />
                  </div>
                  <div className='stat-content'>
                    <p className='stat-title'>משכורות קבלן</p>
                    <h2 className='stat-value'>
                      {formatCurrency(
                        dashboardData?.salaries?.current_month?.kablan_salary ||
                          0
                      )}
                    </h2>
                  </div>
                </div>

                <div className='stat-card'>
                  <div
                    className='stat-icon'
                    style={{ background: "#8b5cf620", color: "#8b5cf6" }}
                  >
                    <Building2 size={24} />
                  </div>
                  <div className='stat-content'>
                    <p className='stat-title'>משכורות חברה</p>
                    <h2 className='stat-value'>
                      {formatCurrency(
                        dashboardData?.salaries?.current_month
                          ?.company_salary || 0
                      )}
                    </h2>
                  </div>
                </div>

                <div className='stat-card'>
                  <div
                    className='stat-icon'
                    style={{ background: "#3b82f620", color: "#3b82f6" }}
                  >
                    <FileText size={24} />
                  </div>
                  <div className='stat-content'>
                    <p className='stat-title'>מספר משכורות</p>
                    <h2 className='stat-value'>
                      {dashboardData?.salaries?.current_month?.salary_count ||
                        0}
                    </h2>
                    <div className='stat-breakdown'>
                      <span>
                        שולם:{" "}
                        {dashboardData?.salaries?.current_month?.paid_count ||
                          0}
                      </span>
                      <span className='divider'>•</span>
                      <span>
                        לא שולם:{" "}
                        {dashboardData?.salaries?.current_month?.unpaid_count ||
                          0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Comparison */}
              {dashboardData?.salaries?.comparison && (
                <div className='comparison-card'>
                  <h3>השוואה לחודש קודם</h3>
                  <div className='comparison-content'>
                    <div className='comparison-item'>
                      <span>חודש נוכחי:</span>
                      <strong>
                        {formatCurrency(
                          dashboardData.salaries.current_month.total
                        )}
                      </strong>
                    </div>
                    <div className='comparison-item'>
                      <span>חודש קודם:</span>
                      <strong>
                        {formatCurrency(
                          dashboardData.salaries.previous_month.total
                        )}
                      </strong>
                    </div>
                    <div
                      className={`comparison-item ${
                        dashboardData.salaries.comparison.total_change >= 0
                          ? "positive"
                          : "negative"
                      }`}
                    >
                      {dashboardData.salaries.comparison.total_change >= 0 ? (
                        <ArrowUpRight size={20} />
                      ) : (
                        <ArrowDownRight size={20} />
                      )}
                      <span>שינוי:</span>
                      <strong>
                        {formatCurrency(
                          Math.abs(
                            dashboardData.salaries.comparison.total_change
                          )
                        )}
                        (
                        {dashboardData.salaries.comparison.total_change_percent.toFixed(
                          1
                        )}
                        %)
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customer Statistics */}
          <div className='dashboard-section'>
            <h2 className='section-title'>
              <Building2 size={24} />
              סטטיסטיקת לקוחות
            </h2>
            <div className='stats-grid'>
              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#8b5cf620", color: "#8b5cf6" }}
                >
                  <Building2 size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>סה"כ לקוחות</p>
                  <h2 className='stat-value'>
                    {dashboardData?.customers?.total || 0}
                  </h2>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#10b98120", color: "#10b981" }}
                >
                  <UserCheck size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>עם עובדים פעילים</p>
                  <h2 className='stat-value'>
                    {dashboardData?.customers?.with_active_employees || 0}
                  </h2>
                </div>
              </div>

              <div className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: "#ef444420", color: "#ef4444" }}
                >
                  <UserX size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>ללא עובדים</p>
                  <h2 className='stat-value'>
                    {dashboardData?.customers?.without_employees || 0}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Top Customers by Hours */}
          

          {/* Customer Hours Breakdown */}
          <div className='dashboard-section'>
            <h2 className='section-title'>
              <Clock size={24} />
              פירוט שעות לפי לקוח - {formatMonthDisplay(selectedMonth)}
            </h2>
            <div className='table-container'>
              <table className='data-table expandable-table'>
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th>שם לקוח / אתר</th>
                    <th>סה"כ שעות</th>
                    <th>שעות קבלן</th>
                    <th>שעות חברה</th>
                    <th>שעות רגילות</th>
                    <th>שעות סופ"ש</th>
                    <th>מספר עובדים</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedCustomerHours.map((customer) => (
                    <React.Fragment key={customer.customer_id}>
                      {/* Customer Row */}
                      <tr
                        className='customer-row'
                        onClick={() =>
                          toggleCustomerExpand(customer.customer_id)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <ChevronRight
                            size={18}
                            style={{
                              transform: expandedCustomers.has(
                                customer.customer_id
                              )
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          />
                        </td>
                        <td className='customer-name'>
                          <strong>{customer.customer_name}</strong>
                          {customer.sites.length > 1 && (
                            <span
                              style={{
                                marginRight: "8px",
                                fontSize: "0.875rem",
                                color: "#64748b",
                              }}
                            >
                              ({customer.sites.length} אתרים)
                            </span>
                          )}
                        </td>
                        <td className='hours'>
                          <strong>{formatNumber(customer.total_hours)}</strong>
                        </td>
                        <td className='hours'>
                          {formatNumber(customer.kablan_hours)}
                        </td>
                        <td className='hours'>
                          {formatNumber(customer.company_hours)}
                        </td>
                        <td className='hours'>
                          {formatNumber(customer.regular_hours)}
                        </td>
                        <td className='hours'>
                          {formatNumber(customer.weekend_hours)}
                        </td>
                        <td className='count'>{customer.employee_count}</td>
                      </tr>

                      {/* Site Rows (Expanded) */}
                      {expandedCustomers.has(customer.customer_id) &&
                        customer.sites.length > 1 &&
                        customer.sites.map((site, idx) => (
                          <tr
                            key={`${customer.customer_id}-${idx}`}
                            className='site-row'
                          >
                            <td></td>
                            <td
                              style={{
                                paddingRight: "2rem",
                                color: "#64748b",
                                fontSize: "0.9rem",
                              }}
                            >
                              ↳ {site.site_name || "ללא שם אתר"}
                            </td>
                            <td className='hours'>
                              {formatNumber(site.total_hours)}
                            </td>
                            <td className='hours'>
                              {formatNumber(site.kablan_hours)}
                            </td>
                            <td className='hours'>
                              {formatNumber(site.company_hours)}
                            </td>
                            <td className='hours'>
                              {formatNumber(site.regular_hours)}
                            </td>
                            <td className='hours'>
                              {formatNumber(site.weekend_hours)}
                            </td>
                            <td className='count'>{site.employee_count}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Customers by Salary */}
          {user?.permission !== "viewer" && (
            <>
            
              {/* Customer Salaries Breakdown */}
              <div className='dashboard-section'>
                <h2 className='section-title'>
                  <DollarSign size={24} />
                  פירוט משכורות לפי לקוח - {formatMonthDisplay(selectedMonth)}
                </h2>
                <div className='table-container'>
                  <table className='data-table expandable-table'>
                    <thead>
                      <tr>
                        <th style={{ width: "40px" }}></th>
                        <th>שם לקוח / אתר</th>
                        <th>סה"כ משכורות</th>
                        <th>משכורות קבלן</th>
                        <th>משכורות חברה</th>
                        <th>מספר עובדים</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedCustomerSalaries.map((customer) => (
                        <React.Fragment key={customer.customer_id}>
                          {/* Customer Row */}
                          <tr
                            className='customer-row'
                            onClick={() =>
                              toggleCustomerExpand(customer.customer_id)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <ChevronRight
                                size={18}
                                style={{
                                  transform: expandedCustomers.has(
                                    customer.customer_id
                                  )
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </td>
                            <td className='customer-name'>
                              <strong>{customer.customer_name}</strong>
                              {customer.sites.length > 1 && (
                                <span
                                  style={{
                                    marginRight: "8px",
                                    fontSize: "0.875rem",
                                    color: "#64748b",
                                  }}
                                >
                                  ({customer.sites.length} אתרים)
                                </span>
                              )}
                            </td>
                            <td className='salary'>
                              <strong>
                                {formatCurrency(customer.total_salary)}
                              </strong>
                            </td>
                            <td className='salary'>
                              {formatCurrency(customer.kablan_salary)}
                            </td>
                            <td className='salary'>
                              {formatCurrency(customer.company_salary)}
                            </td>
                            <td className='count'>{customer.employee_count}</td>
                          </tr>

                          {/* Site Rows (Expanded) */}
                          {expandedCustomers.has(customer.customer_id) &&
                            customer.sites.length > 1 &&
                            customer.sites.map((site, idx) => (
                              <tr
                                key={`${customer.customer_id}-${idx}`}
                                className='site-row'
                              >
                                <td></td>
                                <td
                                  style={{
                                    paddingRight: "2rem",
                                    color: "#64748b",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  ↳ {site.site_name || "ללא שם אתר"}
                                </td>
                                <td className='salary'>
                                  {formatCurrency(site.total_salary)}
                                </td>
                                <td className='salary'>
                                  {formatCurrency(site.kablan_salary)}
                                </td>
                                <td className='salary'>
                                  {formatCurrency(site.company_salary)}
                                </td>
                                <td className='count'>{site.employee_count}</td>
                              </tr>
                            ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Quick Actions */}
          <div className='dashboard-section'>
            <h2 className='section-title'>פעולות מהירות</h2>
            <div className='actions-grid'>
              <Link to='/upload' className='action-card'>
                <div
                  className='action-icon'
                  style={{ background: "#3b82f620", color: "#3b82f6" }}
                >
                  <FileText size={28} />
                </div>
                <h3>העלאת קבצים</h3>
                <p>העלה קבצי T&M או חברות</p>
              </Link>

              <Link to='/employees' className='action-card'>
                <div
                  className='action-icon'
                  style={{ background: "#10b98120", color: "#10b981" }}
                >
                  <Users size={28} />
                </div>
                <h3>ניהול עובדים</h3>
                <p>צפייה ועריכת רשומות עובדים</p>
              </Link>

              <Link to='/salaries/generate' className='action-card'>
                <div
                  className='action-icon'
                  style={{ background: "#f59e0b20", color: "#f59e0b" }}
                >
                  <DollarSign size={28} />
                </div>
                <h3>יצירת משכורות</h3>
                <p>חישוב משכורות חודשיות</p>
              </Link>

              <Link to='/customers' className='action-card'>
                <div
                  className='action-icon'
                  style={{ background: "#8b5cf620", color: "#8b5cf6" }}
                >
                  <Building2 size={28} />
                </div>
                <h3>ניהול לקוחות</h3>
                <p>צפייה ועריכת לקוחות</p>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
